import { all, countBy, join, map, pipe, toPairs } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ActionName } from '../../../../../constants/Bi';
import { ContextMenuId } from '../../../../../constants/ContextMenuId';
import { ITEM_COUNTS_TRANSLATIONS } from '../../../../../constants/ItemType';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';
import {
  useTrackCreateVideoToolClick,
  VideoCreationScript,
} from '../../../../../utils/hooks/bi/useTrackCreateVideoToolClick';
import { useTrackInfoPanelFileActions } from '../../../../../utils/hooks/bi/useTrackInfoPanelFileActions';
import { ClickOrigin as ClickOriginLearnMore } from '../../../../../utils/hooks/bi/useTrackLearnMoreClickAnalytics';
import { ClickOrigin, useTrackTrashActions } from '../../../../../utils/hooks/bi/useTrackTrashActions';
import { useTrackTrashButtons } from '../../../../../utils/hooks/bi/useTrackTrashButtons';
import {
  usePermanentDeleteModalHandlers,
  useShouldShowPermanentDelete,
} from '../../../../../utils/hooks/useModal/modals/usePermanentDeleteModal';
import { useSources } from '../../../../../utils/hooks/useSources';
import { useVideoMaker } from '../../../../../utils/hooks/useVideoMaker';
import { useCanUseItemsInVideoMaker } from '../../../../../utils/hooks/useVideoMaker/useVideoMaker';
import { MediaManagerSDKFile } from '../../../../../utils/media-manager-sdk';
import { isBoard } from '../../../../../utils/media-manager-sdk/boards';
import { getItemType, isItemDownloadable } from '../../../../../utils/media-manager-sdk/items';
import { Text, WixIcons } from '../../../../../components/common';
import { ActionItem } from '../ActionItem';
import { Actions } from '../Actions';
import { BoardInfo } from '../BoardInfo';
import { DeleteFlowPanelInformation } from '../DeleteFlowPanelInformation';
import { ItemName } from '../ItemName';
import { Separator } from '../Separator';
import styles from './MultipleItemsBottomPart.scss';
import { IOuter } from './MultipleItemsBottomPart.types';
import { useFileAwarenessModalHandlers } from '../../../../../utils/hooks/useModal/modals/useFileAwarenessModal';

export const MultipleItemsBottomPart: FC<IOuter> = ({
  items,
  downloadSelectedItems,
  isMoreActionsVisible,
  isRestoreVisible,
  restoreItems,
  isDeleteForeverVisible,
  source,
  onRemoveItemsFromBoard,
}) => {
  const { formatMessage } = useIntl();
  const { isBoards } = useSources({ source });

  const { mmgrSettings } = useMMGRSettings();
  const { isSecureContext, isFileAwarenessEnabled } = mmgrSettings;

  const itemsCountPerType = useMemo(() => pipe(countBy(getItemType), toPairs)(items), [items]);

  const { openVideoMaker } = useVideoMaker();
  const { trackCreateVideoToolClick } = useTrackCreateVideoToolClick();
  const { trackInfoPanelFileAction } = useTrackInfoPanelFileActions();

  const handleVideoMakerClick = useCallback(() => {
    trackInfoPanelFileAction(items, ActionName.CreateVideo);
    trackCreateVideoToolClick(VideoCreationScript.CreateVideo, items as MediaManagerSDKFile[]);
    openVideoMaker(items as MediaManagerSDKFile[]);
  }, [trackCreateVideoToolClick, trackInfoPanelFileAction, openVideoMaker, items]);

  const { trackRestoreButtonClicked, trackDeleteForeverButtonClicked } = useTrackTrashButtons();
  const { trackRestore } = useTrackTrashActions();

  const handleRestoreButtonClick = useCallback(() => {
    trackInfoPanelFileAction(items, ActionName.Restore);
    trackRestoreButtonClicked();
    trackRestore(items, { clickOrigin: ClickOrigin.MoreInfo });
    restoreItems();
  }, [trackRestoreButtonClicked, trackInfoPanelFileAction, trackRestore, items, restoreItems]);

  const { openModal: openDeleteForeverModal } = usePermanentDeleteModalHandlers();
  const { openModal: openFileAwarenessModal } = useFileAwarenessModalHandlers();

  const handleDeleteForeverButtonClick = useCallback(() => {
    if (isFileAwarenessEnabled) {
      openFileAwarenessModal({ clickOrigin: ClickOrigin.MoreInfo, selectedItems: items });
    } else {
      openDeleteForeverModal({ clickOrigin: ClickOrigin.MoreInfo });
    }
    trackInfoPanelFileAction(items, ActionName.DeleteForever);
    trackDeleteForeverButtonClicked();
  }, [
    trackInfoPanelFileAction,
    items,
    trackDeleteForeverButtonClicked,
    isFileAwarenessEnabled,
    openFileAwarenessModal,
    openDeleteForeverModal,
  ]);

  const canUseItemsInVideoMaker = useCanUseItemsInVideoMaker(items);

  const shouldShowPermanentDeleteInfo = useShouldShowPermanentDelete(source);

  const canDownloadSelected = all(isItemDownloadable, items) && !isSecureContext;

  const hasBoardInSelectedItems = useMemo(() => isBoard(items[0]), [items]);

  return (
    <div data-hook="multiple-items-bottom-part">
      {hasBoardInSelectedItems ? (
        <BoardInfo
          name={
            <FormattedMessage
              id="panel.bottom-parts.multiple-items-bottom-part.boards-items-selected"
              values={{ itemsCount: items.length }}
            />
          }
          isMultipleBoardsSelected
        />
      ) : (
        <ItemName
          name={formatMessage(
            {
              id: 'panel.bottom-parts.multiple-items-bottom-part.items-selected',
            },
            { itemsCount: items.length },
          )}
        />
      )}

      {!hasBoardInSelectedItems && (
        <Text
          weight="thin"
          size="small"
          secondary
          tagName="div"
          className={styles.info}
          dataHook="multiple-items-top-part-selection-info"
        >
          {pipe(
            map(([type, count]) => formatMessage({ id: ITEM_COUNTS_TRANSLATIONS[type] }, { count })),
            join(', '),
          )(itemsCountPerType)}
        </Text>
      )}

      {!hasBoardInSelectedItems && <Separator />}

      <Actions
        selectedItems={items}
        className={styles.actions}
        dataHook="actions"
        isMoreActionsVisible={isMoreActionsVisible}
        isMultipleBoardsSelected={hasBoardInSelectedItems}
        contextMenuId={ContextMenuId.SelectedItems}
        source={source}
        onRemoveItemsFromBoard={onRemoveItemsFromBoard}
      >
        {!isBoards && canUseItemsInVideoMaker && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.VideoMaker />}
            label={<FormattedMessage id="actions.create-video" />}
            onClick={handleVideoMakerClick}
            dataHook="actions.create-new-video"
          />
        )}

        {canDownloadSelected && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.Download />}
            label={<FormattedMessage id="actions.download" />}
            onClick={downloadSelectedItems}
            dataHook="actions.download.panel"
          />
        )}

        {isRestoreVisible && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.Revert />}
            label={<FormattedMessage id="actions.restore" />}
            onClick={handleRestoreButtonClick}
            dataHook="actions.restore"
          />
        )}

        {isDeleteForeverVisible && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.EmptyTrash />}
            label={<FormattedMessage id="actions.delete-forever" />}
            onClick={handleDeleteForeverButtonClick}
            dataHook="actions.delete-forever"
          />
        )}
      </Actions>

      {shouldShowPermanentDeleteInfo && <DeleteFlowPanelInformation clickOrigin={ClickOriginLearnMore.Trash} />}
    </div>
  );
};
